import { useTranslation } from "react-i18next"

import type { CollabEntry } from "@/types/collab"

/**
 * OKS-988 — "You already have this product" told to the CREATOR, at `assigned`.
 *
 * The platform has known this since OKS-935, but only told the brand, at assign
 * time. The creator was never told and could not act on it, so we shipped
 * duplicates or lost the participation.
 *
 * ⚠️ PROSE, never a chip or a badge, and deliberately NOT part of the gift row.
 *
 * Two reasons, both load-bearing:
 *
 *  1. A recency fact is a sentence ("you got this three days ago"), and a
 *     coloured token cannot say it. UX §6 requires the fact itself to be
 *     readable, not inferred from a colour.
 *  2. `GiftLine`'s row is guarded by a measured-geometry E2E arm: its three
 *     truncation classes (wrapper `min-w-0`, name `min-w-0 truncate`, value
 *     `shrink-0`) broke invisibly twice during OKS-986. Adding a token to that
 *     row is exactly what the arm exists to catch. This renders BELOW it, as a
 *     sibling, and touches none of its classes.
 *
 * ⚠️ STATIC FLOW CONTENT — no `relative` / `absolute`. A positioned node here
 * paints over the mission card's stretched link and swallows taps on the most
 * persuasive part of the card (OKS-986 D9).
 */
export function RecencyLine({
  entry,
  brandName,
  offerCount = 0,
  testId = "mission-card-recency-line",
}: {
  entry: CollabEntry | null | undefined
  /** Used only by the no-recency-fact variant, which names the brand. */
  brandName: string
  /**
   * OKS-990 decision 4 — how many products this mission offers. `>= 2` adds
   * the clause below.
   *
   * ⚠️ Only the DETAIL surface passes a real count. The card pins it to 0 (see
   * `mission-card.tsx`): UX §S2 places the clause in the page body, and its
   * anchor is `mission-detail-recency-multi-clause`. Defaulting to 0 means a
   * future call site gets the card's behaviour silently — which is why the two
   * shipped ones both pass it explicitly.
   */
  offerCount?: number
  testId?: string
}) {
  const { t } = useTranslation()

  if (!entry?.dual_path) return null

  // Decision 4 applies to a mission offering a real choice; on one product the
  // clause would state a consequence that cannot arise.
  const multi = offerCount >= 2

  // ⚠️ The A12 branch below returns BEFORE the clause, deliberately. With no
  // recency fact there is no "you already have X" for the clause to qualify —
  // its whole sentence is the contrast between the product held and the product
  // picked, and with nothing held it would assert a fact that does not exist.

  // PRD A12 — the brand pre-set "don't ship" for a creator the TTL guard did
  // NOT flag. We still say so: silently no-shipping someone who was never told
  // is the failure this whole ticket is about.
  if (!entry.recency) {
    return (
      <p className="text-muted-foreground text-sm" data-testid={testId}>
        {t("collabs.recency.no_ship_no_fact", { brand: brandName })}
      </p>
    )
  }

  const { product_name: product, days_ago: daysAgo } = entry.recency

  // ⚠️ THREE EXPLICIT KEYS, selected here rather than by an interpolated key
  // name (`recency.${n}`). The InertiaI18n health check's AST scanner cannot
  // trace variable indirection, so a computed key reads as dead and its
  // translation gets swept. French `Intl.PluralRules` also has no zero
  // category, so "today" could not ride a plural rule.
  const message =
    daysAgo === 0
      ? t("collabs.recency.today", { product })
      : daysAgo === 1
        ? t("collabs.recency.one_day", { product })
        : t("collabs.recency.n_days", { product, count: daysAgo })

  return (
    <>
      <p className="text-muted-foreground text-sm" data-testid={testId}>
        {message}
      </p>
      {multi && <MultiProductClause />}
    </>
  )
}

/**
 * OKS-990 decision 4 — the anti-"this is a bug" line (AC5).
 *
 * Recency is MISSION-level: holding ANY offered product triggers the dual path
 * for the whole mission, INCLUDING when the creator picks one they have never
 * received. That is deliberate — the reason textarea is the signal to the brand
 * regardless of which product is requested — and it reads as a defect unless it
 * is named.
 *
 * ⚠️ A SENTENCE IN THE FLOW, not a tooltip and not an info icon (UX §6). Its
 * entire job is that the required reason does not read as a bug, and a fact
 * hidden behind a hover has not been stated.
 */
function MultiProductClause() {
  const { t } = useTranslation()
  return (
    <p
      className="text-muted-foreground text-sm"
      data-testid="mission-detail-recency-multi-clause"
    >
      {t("collabs.recency.multi_product_clause")}
    </p>
  )
}
